import {
  Box,
  Card,
  Container,
  Flex,
  Grid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import {
  ActivityIcon,
  BotIcon,
  CreditCardIcon,
  DollarSignIcon,
  EyeIcon,
  FileEditIcon,
  FileTextIcon,
  FolderIcon,
  HandCoinsIcon,
  LockIcon,
  MoonIcon,
  ReceiptIcon,
  RocketIcon,
  ShieldCheckIcon,
  SparklesIcon,
  WalletIcon,
} from 'lucide-react';
import { ReactNode } from 'react';

const JumboTitle = ({
  children,
  order = 1,
  fz = 'xl',
  ...otherProps
}: {
  children: ReactNode;
  order?: 1 | 2 | 3 | 4 | 5;
  fz?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  [key: string]: any;
}) => {
  const FONT_WEIGHT_MAP: Record<string, number> = {
    xl: 800,
    lg: 800,
    md: 700,
    sm: 600,
    xs: 500,
  };

  const FONT_SIZE_MAP: Record<string, string> = {
    xl: 'clamp(2.5rem, 5vw + 1rem, 4rem)',
    lg: 'clamp(2rem, 4vw + 1rem, 3.5rem)',
    md: 'clamp(1.75rem, 3vw + 1rem, 3rem)',
    sm: 'clamp(1.5rem, 2.5vw + 1rem, 2.5rem)',
    xs: 'clamp(1.25rem, 2vw + 1rem, 2rem)',
  };

  const { style: otherStyle, ...restProps } = otherProps;

  return (
    <Title
      fw={FONT_WEIGHT_MAP[fz]}
      lh={1}
      order={order}
      style={{
        fontSize: FONT_SIZE_MAP[fz],
        ...otherStyle,
      }}
      {...restProps}
    >
      {children}
    </Title>
  );
};

interface Feature {
  icon: ReactNode;
  title: string;
  description: string;
}

const FEATURES: Feature[] = [
  {
    icon: <RocketIcon size={20} />,
    title: 'Streamline Client Onboarding',
    description:
      'Automated engagement letter generation tailored to each specific case.',
  },
  {
    icon: <EyeIcon size={20} />,
    title: 'Case information at a glance',
    description:
      'Get all the case details you need in one intuitive dashboard view.',
  },
  {
    icon: <FolderIcon size={20} />,
    title: 'Easy document management',
    description:
      'Organize and access all your case documents in one centralized location.',
  },
  {
    icon: <ReceiptIcon size={20} />,
    title: 'Invoicing with the click of a button',
    description:
      'Generate professional invoices instantly from your time entries and expenses.',
  },
  {
    icon: <HandCoinsIcon size={20} />,
    title: 'Easily accept alternative fees',
    description:
      'Support flat fees, contingency, and other alternative fee structures with ease.',
  },
  {
    icon: <ShieldCheckIcon size={20} />,
    title: 'Legal insurance made easy',
    description:
      'Accept cases and file claims with legal insurance companies in just a few clicks.',
  },
  {
    icon: <SparklesIcon size={20} />,
    title: 'AI Assistant',
    description:
      'Get help with tasks and documents from an AI assistant that knows your system.',
  },
  {
    icon: <MoonIcon size={20} />,
    title: 'Dark mode',
    description:
      'Built-in dark mode support to reduce eye strain on long days and late nights.',
  },
  {
    icon: <FileEditIcon size={20} />,
    title: 'AI document drafting',
    description:
      'Draft documents faster with AI-powered template filling that understands context.',
  },
  {
    icon: <WalletIcon size={20} />,
    title: 'Trust Accounting',
    description:
      'Manage client trust accounts with automatic, real-time ledgers.',
  },
  {
    icon: <LockIcon size={20} />,
    title: 'Secure Cloud Database',
    description:
      'Enterprise-grade security with industry-standard encryption and compliance.',
  },
  {
    icon: <ActivityIcon size={20} />,
    title: '99.9% Uptime Guarantee',
    description:
      'Reliable access to your practice management system when you need it.',
  },
] as const;

interface FeatureCellProps extends Feature {
  iconSize?: number;
}

const FeatureCell = ({
  icon,
  title,
  description,
  iconSize = 20,
}: FeatureCellProps) => (
  <Card radius='lg' p='xl' h='100%'>
    <Stack gap='xs'>
      <Flex w={iconSize} h={iconSize} justify='center' align='center'>
        {icon}
      </Flex>
      <Box>
        <Text fz='xl'>{title}</Text>
        <Text fz='md' c='dimmed'>
          {description}
        </Text>
      </Box>
    </Stack>
  </Card>
);

interface FeatureListProps {
  title?: string;
  features?: Feature[];
  iconSize?: number;
}

const FeatureList = ({
  title = 'Features',
  features = FEATURES,
  iconSize = 20,
}: FeatureListProps) => (
  <Container
    bg='var(--mantine-color-body)'
    py={{
      base: 'calc(var(--mantine-spacing-lg) * 4)',
      xs: 'calc(var(--mantine-spacing-lg) * 5)',
      lg: 'calc(var(--mantine-spacing-lg) * 6)',
    }}
    fluid
  >
    <Container size='lg' px={0}>
      <JumboTitle order={2} fz='md' ta='center' style={{ textWrap: 'balance' }}>
        {title}
      </JumboTitle>
    </Container>
    <Container size='lg' p={0} mt='xl'>
      <Grid gutter='sm'>
        {features.map((feature, index) => (
          <Grid.Col
            key={feature.title}
            span={{ base: 12, xs: 6, md: 4 }}
            mih='100%'
          >
            <FeatureCell
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              iconSize={iconSize}
            />
          </Grid.Col>
        ))}
      </Grid>
    </Container>
  </Container>
);

export default FeatureList;
